import { useMemo, useState } from 'react';
import LoadingButton from '@mui/lab/LoadingButton';
import TextField from '@mui/material/TextField';
import Link from '@mui/material/Link';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import {
  Autocomplete,
  DialogContent,
  DialogActions,
  Dialog,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  useMediaQuery,
  DialogTitle,
  FormControlLabel,
  RadioGroup,
  Radio,
  IconButton
} from '@mui/material';
import Copyright from '../../components/auth/Copyright';
import chabadLogo from '../../assets/reshet.png';
import { CheckCircleOutline, Close } from '@mui/icons-material';
import { AutocompleteType } from '@/context/context';
import instanceAxios from '@/util/axios';
import waCode from '../../assets/wa-code.png';

import { phonePrefix as phonePrefixData, registerMfaUrl, verifyPhoneUrl } from '@/constants';
import { theme } from '@/theme/theme';
import PinCode from '@/components/auth/PinCode';
import ResendMfa from '@/components/auth/ResendMfa';
import { StaffMemberRole } from '@sm-dashboard/reshet-types';
import { useLocation } from 'react-router-dom';
import { useInstitutionsPublic } from '@/api/institutions';
import { queryClient } from '@/react-query';

const Register = () => {
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const { state } = useLocation();

  const [firstName, setFirstName] = useState(state?.firstName || '');
  const [lastName, setLastName] = useState(state?.lastName || '');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState(state?.email || '');
  const [selectedInstitution, setSelectedInstitution] = useState<AutocompleteType | null>(null);
  const [phonePrefix, setPhonePrefix] = useState('');
  const [role, setRole] = useState('');
  const [errorPhone, setErrorPhone] = useState('');
  const [isWhatsApp, setIsWhatsApp] = useState(true);
  const [loading, setLoading] = useState(false);
  const [readyPinCode, setReadyPinCode] = useState<boolean>(false);
  const [errorTxt, setErrorTxt] = useState<string>('');
  const [pinCode, setPinCode] = useState<string>('');
  const [errCode, setErrCode] = useState<string>('');
  const [reSecret, setReSecret] = useState<string | null>(null);
  const [mode, setMode] = useState<'fillForm' | 'picVerifyPhone' | 'verifyPhone' | 'completed'>('fillForm');

  const { institutions } = useInstitutionsPublic();

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsWhatsApp(event.target.value === 'whatsapp');
  };

  const handleClearError = () => {
    setErrorPhone('');
  };

  const onSubmit = async () => {
    setLoading(true);

    try {
      await instanceAxios.post(verifyPhoneUrl, {
        onlyCheck: true,
        phone: `${phonePrefix}${phone}`,
        isWhatsApp
      });

      queryClient.invalidateQueries();

      setMode('picVerifyPhone');
      setLoading(false);
    } catch (error) {
      setErrorPhone('מספר הטלפון קיים במערכת חב״ד ישראל על חשבון אחר');
      setLoading(false);
    }
  };

  const onVerifyPhone = async () => {
    setLoading(true);
    try {
      const res = await instanceAxios.post(verifyPhoneUrl, {
        onlyCheck: false,
        phone: `${phonePrefix}${phone}`,
        isWhatsApp
      });
      queryClient.invalidateQueries();

      setReSecret(res?.data?.secret);
      setMode('verifyPhone');
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  const onVerifyCode = async () => {
    if (!readyPinCode) {
      setErrCode('error');
      return;
    }
    setErrCode('');
    setLoading(true);
    try {
      await instanceAxios.post(registerMfaUrl, {
        phone: `${phonePrefix}${phone}`,
        code: pinCode,
        secret: reSecret,
        firstName,
        lastName,
        email,
        role,
        parentId: Number(selectedInstitution?.value),
        type: role === 'מנהל/ת גנים' ? 'רשת' : 'עובד'
      });
      setMode('completed');
      setLoading(false);
    } catch (error) {
      setLoading(false);
      setErrCode('error');
      setErrorTxt('קוד לא תואם למספר שנשלח');
    }
  };

  const institutionsOptions = useMemo(
    () =>
      institutions.map(institution => ({
        value: institution.value.toString(),
        label: institution.label
      })),
    [institutions]
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        backgroundColor: 'background.paper',
        justifyContent: 'center',
        paddingBlock: '20px',
        height: '100vh',
        boxSizing: 'border-box'
      }}
    >
      <img src={chabadLogo} alt="logo" style={{ width: '170px', marginBottom: '20px' }} />

      {mode === 'completed' && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 3,
            p: 4,
            backgroundColor: 'background.paper'
          }}
        >
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              backgroundColor: 'success.light',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <CheckCircleOutline sx={{ color: '#fff', fontSize: 50 }} />
          </Box>
          <Typography variant="h6" component="div" align="center" color="primary">
            תודה על ההרשמה
            <br />
            צוות האתר ישלים את ההרשמה בקרוב <br></br> וישלח הודעה נוספת על כך
          </Typography>
          <Link href="/login" variant="body2">
            להתחברות
          </Link>
        </Box>
      )}

      {mode === 'fillForm' && (
        <>
          <Typography component="h1" variant="h5" color="primary">
            הרשמה
          </Typography>
          <Box sx={{ p: 2, backgroundColor: 'background.paper' }}>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 0, sm: 2 } }}>
              <TextField
                size={isSmallScreen ? 'small' : 'medium'}
                margin="normal"
                required
                fullWidth
                id="firstName"
                label="שם פרטי"
                name="firstName"
                autoComplete="firstName"
                autoFocus
                value={firstName}
                onChange={e => setFirstName(e.target.value)}
                // sx={{ paddingY: '7px' }}
              />
              <TextField
                size={isSmallScreen ? 'small' : 'medium'}
                margin="normal"
                required
                fullWidth
                id="name"
                label="שם משפחה"
                name="lastName"
                autoComplete="lastName"
                value={lastName}
                onChange={e => setLastName(e.target.value)}
              />
            </Box>

            <TextField
              size={isSmallScreen ? 'small' : 'medium'}
              margin="normal"
              required
              fullWidth
              id="email"
              label="דוא״ל"
              name="email"
              autoComplete="email"
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
            />

            <Box
              sx={{
                display: 'flex',
                gap: 2,
                alignItems: 'center',
                justifyContent: 'center',
                direction: 'ltr'
              }}
            >
              <TextField
                size={isSmallScreen ? 'small' : 'medium'}
                required
                fullWidth
                name="phone"
                label="מספר טלפון"
                inputProps={{
                  maxLength: 7,
                  pattern: '[0-9]*'
                }}
                type="tel"
                error={!!errorPhone}
                helperText={!isSmallScreen && errorPhone}
                margin="normal"
                value={phone}
                onChange={e => {
                  const numericValue = e.target.value.replace(/[^0-9]/g, '');
                  setPhone(numericValue);
                  handleClearError();
                }}
              />
              <FormControl
                sx={{ width: '150px', ...(!isSmallScreen && { mb: errorPhone ? 3.8 : 0.8 }) }}
                size={isSmallScreen ? 'small' : 'medium'}
                margin="normal"
                fullWidth
              >
                <InputLabel id="role-label">קידומת</InputLabel>
                <Select
                  size={isSmallScreen ? 'small' : 'medium'}
                  labelId="phone-prefix-label"
                  id="phone-prefix-id"
                  value={phonePrefix}
                  error={!!errorPhone}
                  label="קידומת"
                  onChange={e => {
                    setPhonePrefix(e.target.value);
                    handleClearError();
                  }}
                >
                  {phonePrefixData.map(prefix => (
                    <MenuItem key={prefix.code} value={prefix.code}>
                      {prefix.code}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <FormControl size={isSmallScreen ? 'small' : 'medium'} margin="normal" fullWidth>
              <InputLabel id="role-label">תפקיד</InputLabel>
              <Select
                size={isSmallScreen ? 'small' : 'medium'}
                labelId="role-label"
                id="role-id"
                value={role}
                label="תפקיד"
                onChange={e => setRole(e.target.value)}
              >
                {[
                  StaffMemberRole.TEACHER,
                  StaffMemberRole.KINDERGARTEN_TEACHER,
                  StaffMemberRole.ASSISTANT_KINDERGARTEN_TEACHER,
                  StaffMemberRole.REGISTRATION_MANAGER,
                  StaffMemberRole.SUPERVISOR,
                  StaffMemberRole.MANAGER
                ].map(role => (
                  <MenuItem key={role} value={role}>
                    {role}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box position="relative">
              <Autocomplete
                disablePortal
                blurOnSelect
                size={isSmallScreen ? 'small' : 'medium'}
                slotProps={{
                  clearIndicator: { sx: { right: 30, top: -12, position: 'absolute' } },
                  popupIndicator: { sx: { right: 0, top: -12, position: 'absolute' } }
                }}
                PaperComponent={({ children }) => (
                  <Box
                    component="div"
                    sx={{
                      boxShadow: '0px 5px 15px rgba(0, 0, 0,0.2)',
                      borderRadius: 1,
                      opacity: 1,
                      backgroundColor: 'background.default'
                    }}
                  >
                    {children}
                  </Box>
                )}
                options={institutionsOptions}
                isOptionEqualToValue={(option, value) => option.value === value.value}
                getOptionKey={option => option.value}
                onChange={(event, value) => {
                  if (value) {
                    setSelectedInstitution(value);
                  }
                }}
                value={selectedInstitution}
                id="institution-id"
                getOptionLabel={(option: AutocompleteType) => option.label}
                renderInput={params => (
                  <TextField helperText="רישום לפעילות תחת מוסד" {...params} margin="normal" label="מוסד" fullWidth />
                )}
                autoComplete={false}
              />
            </Box>

            {isSmallScreen && errorPhone && (
              <Box sx={{ width: '100%', mt: 0.5, textAlign: 'center' }}>
                <Typography variant="caption" color="error">
                  {errorPhone}
                </Typography>
              </Box>
            )}

            <LoadingButton
              size={isSmallScreen ? 'small' : 'medium'}
              loading={mode === 'fillForm' && loading}
              fullWidth
              variant="contained"
              disabled={!phone || !phonePrefix || !role || !selectedInstitution || !firstName || !lastName || !email}
              sx={{ mt: 1, mb: 1 }}
              onClick={onSubmit}
            >
              הרשמה
            </LoadingButton>

            <Box
              sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, flexDirection: 'column' }}
            >
              <Link sx={{ textAlign: 'center' }} href="/login" variant="body2">
                {'כבר יש לך חשבון? התחבר'}
              </Link>
              <Copyright />
            </Box>
          </Box>
        </>
      )}
      <Dialog
        sx={{ textAlign: 'center' }}
        fullWidth
        maxWidth="xs"
        open={mode === 'picVerifyPhone' || mode === 'verifyPhone'}
        onClose={() => setMode('fillForm')}
      >
        <DialogTitle sx={{ position: 'relative' }} color="primary">
          אימות מספר טלפון
          <Box sx={{ display: 'flex', justifyContent: 'flex-start', position: 'absolute', top: 0, left: 0 }}>
            <IconButton onClick={() => setMode('fillForm')} size="small">
              <Close fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        {mode === 'picVerifyPhone' ? (
          <>
            <DialogContent>
              <Typography variant="body1">בחר אימות באמצעות</Typography>
              <FormControl sx={{ mt: 2 }}>
                <RadioGroup
                  row
                  aria-labelledby="message-type-radio-group"
                  defaultValue={isWhatsApp ? 'whatsapp' : 'sms'}
                  name="message-type-radio-buttons"
                  onChange={handleChange}
                >
                  <FormControlLabel value="whatsapp" control={<Radio />} label="וואטסאפ" />
                  <FormControlLabel value="sms" control={<Radio />} label="סמס" />
                </RadioGroup>
              </FormControl>
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'space-around' }}>
              <LoadingButton
                loading={mode === 'picVerifyPhone' && loading}
                fullWidth
                variant="contained"
                onClick={onVerifyPhone}
              >
                קבל קוד לנייד
              </LoadingButton>
            </DialogActions>
          </>
        ) : (
          <>
            <DialogContent>
              <Typography
                sx={{ marginTop: 1, marginBottom: 1 }}
                component="h1"
                variant="subtitle1"
                color="text.primary"
              >
                {isWhatsApp ? 'הכנס קוד שנשלח לוואטסאפ' : 'הכנס קוד שנשלח למספר טלפון'}
              </Typography>

              {isWhatsApp && <img src={waCode} alt="wa-code" style={{ width: '100%', borderRadius: 10 }} />}

              <Box sx={{ mt: 3, mb: 2 }}>
                <PinCode
                  length={6}
                  onChange={value => setReadyPinCode(value.length === 6)}
                  onComplete={value => setPinCode(value)}
                  onError={errCode}
                  direction="rtl"
                />
              </Box>
              <Typography>
                קוד נשלח למספר ****{`${phonePrefix}${phone}`?.slice(0, `${phonePrefix}${phone}`.length - 4)}
              </Typography>
              {errorTxt && (
                <>
                  <Typography sx={{ marginTop: 1 }} component="h1" variant="subtitle2" color="primary">
                    {errorTxt}
                  </Typography>
                  <ResendMfa phone={`${phonePrefix}${phone}`} setReSecret={setReSecret} isWhatsApp={isWhatsApp} />
                </>
              )}
            </DialogContent>
            <DialogActions>
              <LoadingButton
                fullWidth
                type="submit"
                onClick={onVerifyCode}
                variant="contained"
                loading={mode === 'verifyPhone' && loading}
              >
                אימות מספר ורישום{' '}
              </LoadingButton>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default Register;
